import { useState } from "react";
import { ChartColumn } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ChartExpandModal } from "./ChartExpandModal";
import { ChartTitleRow } from "./ChartTitleRow";
import {
  PRIMARY_RED_BAR,
  PRIMARY_RED_BAR_BORDER,
  PRIMARY_RED_BAR_HOVER,
  formatCompactNumber,
} from "./chartPalette";
import type {
  ProjectCount,
  ProjectCategoryBreakdown,
} from "@/hooks/buildings/useBuildingDocumentStats";
import type { Translations } from "@/lib/i18n/types";

interface DocumentsPerProjectChartProps {
  data: ProjectCount[];
  categoryBreakdown: Map<number, ProjectCategoryBreakdown[]>;
  isLoading: boolean;
  t: Translations;
}

/** Recharts clones `content` with `active`/`payload`; the rest are ours. */
interface ProjectTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: ProjectCount }>;
  categoryBreakdown: Map<number, ProjectCategoryBreakdown[]>;
  t: Translations;
}

function CustomTooltipContent({ active, payload, categoryBreakdown, t }: ProjectTooltipProps) {
  const entry = payload?.[0]?.payload;
  if (!active || !entry) return null;

  const allCategories = categoryBreakdown.get(entry.projectId) ?? [];
  const d = t.buildingDetail.dashboard;

  const MAX_CATEGORIES = 9;
  let categories: ProjectCategoryBreakdown[];
  if (allCategories.length > MAX_CATEGORIES + 1) {
    const top = allCategories.slice(0, MAX_CATEGORIES);
    const otherCount = allCategories.slice(MAX_CATEGORIES).reduce((sum, c) => sum + c.count, 0);
    categories = [...top, { projectId: entry.projectId, categoryName: d.other, count: otherCount }];
  } else {
    categories = allCategories;
  }

  const maxCount = categories.length > 0 ? Math.max(...categories.map((c) => c.count)) : 0;

  return (
    <div className="rounded-lg border bg-white p-3 shadow-md" style={{ minWidth: 200 }}>
      <div className="mb-2 text-xs font-semibold">{entry.projectName}</div>
      {categories.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {categories.map((cat, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-20 flex-shrink-0 truncate text-muted-foreground">
                {cat.categoryName}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: maxCount > 0 ? `${(cat.count / maxCount) * 100}%` : "0%",
                    backgroundColor: PRIMARY_RED_BAR_BORDER,
                  }}
                />
              </div>
              <span className="w-6 text-right font-medium tabular-nums">{cat.count}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between border-t pt-2 text-xs font-semibold">
        <span>{d.tooltipTotal}</span>
        <span>{entry.count}</span>
      </div>
    </div>
  );
}

function ProjectBarChart({
  data,
  categoryBreakdown,
  height,
  t,
}: {
  data: ProjectCount[];
  categoryBreakdown: Map<number, ProjectCategoryBreakdown[]>;
  /** Fixed pixel height (used by the modal). Omit to fill the parent's available height. */
  height?: number;
  t: Translations;
}) {
  if (data.length === 0) {
    return (
      <div
        style={height ? { height } : undefined}
        className="flex h-full min-h-[160px] flex-col items-center justify-center gap-1 text-center"
      >
        <ChartColumn className="mb-1 h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm font-medium text-neutral-700">
          {t.buildingDetail.dashboard.noDocuments}
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height ?? "100%"} minHeight={160}>
      <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f3f5" />
        <XAxis
          dataKey="projectName"
          tick={{ fontSize: 11, fill: "#808080" }}
          axisLine={false}
          tickLine={false}
          interval={0}
          tickFormatter={(name: string) => (name.length > 15 ? name.slice(0, 15) + "..." : name)}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: "#808080" }}
          axisLine={false}
          tickLine={false}
          width={40}
          tickFormatter={formatCompactNumber}
        />
        <Tooltip
          content={<CustomTooltipContent categoryBreakdown={categoryBreakdown} t={t} />}
          cursor={{ fill: "rgba(0,0,0,0.03)" }}
        />
        <Bar
          dataKey="count"
          fill={PRIMARY_RED_BAR}
          stroke={PRIMARY_RED_BAR_BORDER}
          strokeWidth={1}
          radius={[4, 4, 0, 0]}
          activeBar={{ fill: PRIMARY_RED_BAR_HOVER }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DocumentsPerProjectChart({
  data,
  categoryBreakdown,
  isLoading,
  t,
}: DocumentsPerProjectChartProps) {
  const [expanded, setExpanded] = useState(false);
  const d = t.buildingDetail.dashboard;

  return (
    <>
      <div className="flex h-full flex-col">
        <ChartTitleRow
          title={d.documentsPerProject}
          onExpand={() => setExpanded(true)}
          expandLabel={d.expandChart}
        />
        {isLoading ? (
          <div className="flex h-[160px] flex-1 items-center justify-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          <div className="flex flex-1 flex-col pt-3">
            <ProjectBarChart data={data} categoryBreakdown={categoryBreakdown} t={t} />
          </div>
        )}
      </div>

      <ChartExpandModal open={expanded} onOpenChange={setExpanded} title={d.documentsPerProject}>
        <ProjectBarChart data={data} categoryBreakdown={categoryBreakdown} height={300} t={t} />
      </ChartExpandModal>
    </>
  );
}
